/**
 * products - defines the behavior of weather data grids, including grid construction, interpolation, and color scales.
 *
 * Copyright (c) 2014 Cameron Beccario
 * The MIT License - http://opensource.org/licenses/MIT
 *
 * https://github.com/cambecc/earth
 */
import µ from "./micro";
import _ from "underscore";
import when from "../../lib/when/2.6.0/when";
import * as d3 from "d3";
import staticVariable from "../../component/staticVariable";
export default (function () {

    var catalogs = {
        // The OSCAR catalog is an array of file names, sorted and prefixed with yyyyMMdd. Last item is the
        // most recent. For example: [ 20140101-abc.json, 20140106-abc.json, 20140112-abc.json, ... ]
        oscar: µ.loadJson([staticVariable.OSCAR_PATH, "catalog.json"].join("/"))
    };

    var log = µ.log();
    const windColorSegments = [
        [0, [0,0,255]],
        [10, [0,195,163]],
        [20, [45,250,0]],
        [30, [230,110,0]],
        [40, [235,0,98]],
        [50, [187,25,187]],
        [60, [200,70,200]],
        [70, [214,117,214]],
        [80, [228,168,228]],
        [90, [241,210,241]],
        [100, [255,255,255]]
    ];
    const tempColorSegments = [
        [0,     [250, 250, 250]],
        [193,     [37, 4, 42]],
        [206,     [41, 10, 130]],
        [219,     [81, 40, 40]],
        [233.15,  [192, 37, 149]],  // -40 C/F
        [255.372, [70, 215, 215]],  // 0 F
        [273.15,  [21, 84, 187]],   // 0 C
        [275.15,  [24, 132, 14]],   // just above 0 C
        [291,     [247, 251, 59]],
        [295,     [235, 167, 21]],
        [303,     [230, 71, 39]],
        [318,     [88, 27, 67]]
    ];

    const seaIceAreaColorSegments = [
        //[0, [0, 0, 0]],
        [0, [20, 20, 20]],
        [20, [41, 10, 130]],
        [40, [21, 84, 187]],
        [70, [25, 255, 255]],
        [95, [255, 255, 255]],
        [100, [255, 255, 255]]
    ];

    const totalPrecipitableWaterColorSegments = [
        //[0, [50, 140, 160]],
        //[0.5, [50, 140, 160]],
        //[5, [220, 235, 245]],
        //[10, [21, 84, 187]],
        //[15, [41, 10, 130]],
        //[20, [250, 0, 100]],
        //[30, [235, 167, 21]],
        //[40, [247, 251, 59]],
        //[45, [215, 255, 200]],
        //[50, [245, 255, 248]]
        [0, [20, 20, 20]],
        [0.1, [230, 71, 39]],
        [0.2, [230, 165, 30]],
        [0.5, [120, 100, 95]],
        [1, [40, 44, 92]],
        [2.5, [21, 13, 193]],
        [5, [75, 63, 235]],
        [10, [25, 255, 255]],
        [20, [245, 255, 248]],
        [30, [255, 255, 255]],
        [50, [255, 0, 255]]
    ];

    const currentsColorSegmentrs = [
        [0, [10, 25, 68]],
        [0.1, [10, 25, 250]],
        [0.25, [24, 255, 93]],
        [0.4, [255, 233, 102]],
        [0.65, [255, 233, 15]],
        [0.8, [255, 15, 15]],
        [1.5, [255, 255, 255]]
    ];

    const od550aerColorSegments = [
        // [0, [0xfa, 0xfa, 0xd2]],
        // [0.5, [0xfa, 0xf0, 0x82]],
        // [0.8, [0xff, 0xe6, 0x41]],
        // [1.0, [0xff, 0xbb, 0x32]],
        // [1.3, [0xff, 0x82, 0x00]]
        [0, [0xfa, 0xfa, 0xd2]],
        [0.3, [0xff, 0xff, 0x41]],
        [0.5, [0xff, 0xe6, 0x41]],
        [0.8, [0xff, 0x8c, 0x32]],
        [1.0, [0xa5, 0x2a, 0x2a]],
        [1.3, [0xff, 0x00, 0x00]]
    ];

    const salntColorSegments = [
        // [5, [0x00, 0x33, 0xff]],
        [15, [0x33, 0x66, 0xff]],
        [23, [0x66, 0x99, 0xff]],
        [32, [0x66, 0xff, 0xff]],
        [35, [0x99, 0xff, 0x99]],
        [38, [0xff, 0x66, 0x00]],
        [41, [0xff, 0x33, 0x00]]
    ];

    const sstColorSegments = [
        [-2, [0x00, 0x33, 0xff]],
        [4, [0xff, 0xff, 0x41]],
        [11, [0xff, 0xe6, 0x41]],
        [17, [0xff, 0x8c, 0x32]],
        [25, [0xcc, 0x33, 0x33]],
        [31, [0xcc, 0x00, 0x00]]
    ];

    const slrColorSegments = [
        // [5, [0xfa, 0xfa, 0xd2]],
        [-1.7, [0x00, 0x66, 0x33]],
        [-1.2, [0x00, 0x99, 0x33]],
        [-0.7, [0x00, 0xff, 0xff]],
        [-0.2, [0x00, 0x99, 0xff]],
        [0, [0x00, 0x66, 0xff]],
        [0.2, [0x33, 0x00, 0x99]],
        [0.5, [0x33, 0x00, 0x66]],
        [0.8, [0x33, 0x00, 0x33]]
    ];

    function buildProduct(overrides) {
        return _.extend({
            description: "",
            paths: [],
            date: null,
            navigate: function(step, scenario) {
                return gfsStep(this.date, step, scenario);
            },
            load: function(cancel) {
                var me = this;
                return when.map(this.paths, µ.loadJson).then(function(files) {
                    return cancel.requested ? null : _.extend(me, buildGrid(me.builder.apply(me, files)));
                });
            }
        }, overrides);
    }

    /**
     * @param attr
     * @param {String} type
     * @param {String?} surface
     * @param {String?} level
     * @returns {String}
     */
    function gfs1p0degPath(attr, type, surface, level) {
        var currentYear = µ.dateToUTCymd(gfsDate(attr), "").substring(0, 4);
        var currentMonth = µ.dateToUTCymd(gfsDate(attr), "").substring(4, 6);
        var currentDir = [currentYear, currentMonth, "01"].join("/");
        var dir = attr.date, stamp = dir === "current" ? "current" : attr.hour;
        var year = dir.substring(0, 4);
        var hist = "hist";
        var scenario = attr.scenario;

        if (dir === "current"){
            scenario = "/" + scenario;
        } else {
            scenario = year > 2014 ? "/" + scenario : "/" + hist;
        }
        if(dir === "current"){
            dir = currentDir;
            stamp = "0000";
        }
        var file = [stamp, type, surface, level, "gfs", "1.0"].filter(µ.isValue).join("-") + ".json";
        return [staticVariable.WEATHER_PATH + scenario, dir, file].join("/");
    }

    function gfsDate(attr) {
        if (attr.date === "current") {
            // Construct the date from the current time, rounding down to the nearest three-hour block.
            var now = new Date(Date.now()), hour = Math.floor(now.getUTCHours() / 3);
            return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour));
        }
        var parts = attr.date.split("/");
        return new Date(Date.UTC(+parts[0], parts[1] - 1, +parts[2], +attr.hour.substr(0, 2)));
    }

    /**
     * Returns a date for the chronologically next or previous GFS data layer. How far forward or backward in time
     * to jump is determined by the step. Steps of ±1 move in 3-hour jumps, and steps of ±10 move in 24-hour jumps.
     */
    /*
     * sainy! 3hour, 1day -> 1month, 1year
     */
    function gfsStep(date, step, scenario) {
        //var offset = (step > 1 ? 8 : step < -1 ? -8 : step) * 3, adjusted = new Date(date);
        //adjusted.setHours(adjusted.getHours() + offset);
        //return adjusted;
        //
        //sainy!!
        var offset = (step > 1 ? 8 : step < -1 ? -8 : step) * 3, adjusted = new Date(date);
        const yearMin = 1850;
        const yearMax = 2100;
        if (step === 10 || step === -10) {
            // adjusted.setUTCFullYear(adjusted.getUTCFullYear() + step/10);
            adjusted.setUTCFullYear(µ.clamp(adjusted.getUTCFullYear() + step/10, yearMin, yearMax));
        }
        else if (step === 1 || step === -1) {
            adjusted.setUTCMonth(adjusted.getUTCMonth() + step);
        }
        else if (step === 100 || step === -100) {
            // adjusted.setUTCFullYear(adjusted.getUTCFullYear() + step*20/100);
            adjusted.setUTCFullYear(µ.clamp(adjusted.getUTCFullYear() + step*20/100, yearMin, yearMax));
        }
        return adjusted;
    }

    function netcdfHeader(time, lat, lon, center) {
        return {
            lo1: lon.sequence.start,
            la1: lat.sequence.start,
            dx: lon.sequence.delta,
            dy: -lat.sequence.delta,
            nx: lon.sequence.size,
            ny: lat.sequence.size,
            refTime: time.data[0],
            forecastTime: 0,
            centerName: center
        };
    }

    function describeSurface(attr) {
        return attr.surface === "surface" ? "Surface" : µ.capitalize(attr.level);
    }

    function describeSurfaceJa(attr) {
        return attr.surface === "surface" ? "地上" : µ.capitalize(attr.level);
    }

    /**
     * Returns a function f(langCode) that, given table:
     *     {foo: {en: "A", ja: "あ"}, bar: {en: "I", ja: "い"}}
     * will return the following when called with "en":
     *     {foo: "A", bar: "I"}
     * or when called with "ja":
     *     {foo: "あ", bar: "い"}
     */
    function localize(table) {
        return function(langCode) {
            var result = {};
            _.each(table, function(value, key) {
                result[key] = value[langCode] || value.en || value;
            });
            return result;
        }
    }

    var FACTORIES = {

        "wind": {
            matches: _.matches({param: "wind"}),
            create: function(attr) {
                return buildProduct({
                    field: "vector",
                    type: "wind",
                    description: localize({
                        name: {en: "Wind", kr: "풍속", ja: "風速"},
                        qualifier: {en: " @ " + describeSurface(attr), ja: " @ " + describeSurfaceJa(attr)}
                    }),
                    paths: [gfs1p0degPath(attr, "wind", attr.surface, attr.level)],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var uData = file[0].data, vData = file[1].data;
                        return {
                            header: file[0].header,
                            interpolate: bilinearInterpolateVector,
                            data: function(i) {
                                return [uData[i], vData[i]];
                            }
                        }
                    },

                    units: getUnitList("wind"),
                    scale: {
                        bounds: [0, 100],
                        customBounds: customBounds("wind"),
                        colorSegments: windColorSegments,
                        gradient: µ.segmentedColorScale(windColorSegments, customSegments("wind"))
                        // gradient: function(v, a) {
                        //     return µ.extendedSinebowColor(Math.min(v, 100) / 100, a);
                        // }
                    },
                    particles: {velocityScale: 1/60000, maxIntensity: 17},
                    vertical: true,
                });
            }
        },

        "temp": {
            matches: _.matches({param: "wind", overlayType: "temp"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "temp",
                    description: localize({
                        name: {en: "Temp", kr: "기온", ja: "気温"},
                        // name: {en: "Temperature", kr: "기온", ja: "気温"},
                        //name: {en: "Temp", ja: "気温"},
                        qualifier: {en: " @ " + describeSurface(attr), ja: " @ " + describeSurfaceJa(attr)}
                    }),
                    paths: [gfs1p0degPath(attr, "temp", attr.surface, attr.level)],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("temp"),
                    scale: {
                        bounds: [193, 318],
                        customBounds: customBounds("temp"),
                        colorSegments: tempColorSegments,
                        gradient: µ.segmentedColorScale(tempColorSegments, customSegments("temp"))
                    },
                    vertical: true,
                });
            }
        },
        "total_precipitable_water": {
            matches: _.matches({param: "wind", overlayType: "total_precipitable_water"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "total_precipitable_water",
                    description: localize({
                        name: {en: "Prec", kr: "강수", ja: "可降水量"},
                        // name: {en: "Total Precipitation Rate", kr: "강수", ja: "可降水量"},
                        //name: {en: "Total Precipitable Water", ja: "可降水量"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "total_precipitable_water")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("total_precipitable_water"),
                    scale: {
                        bounds: [0, 50],
                        customBounds: customBounds("total_precipitable_water"),
                        colorSegments: totalPrecipitableWaterColorSegments,
                        gradient:
                            µ.segmentedColorScale(totalPrecipitableWaterColorSegments, customSegments("total_precipitable_water"))
                    }
                });
            }
        },
        "od550aer": {
            matches: _.matches({param: "wind", overlayType: "od550aer"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "od550aer",
                    description: localize({
                        name: {en: "aerosol", kr: "미세먼지", ja: "PM2.5"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "od550aer")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("od550aer"),
                    scale: {
                        bounds: [0, 1.2],
                        customBounds: customBounds("od550aer"),
                        colorSegments: od550aerColorSegments,
                        gradient:
                            µ.segmentedColorScale(od550aerColorSegments, customSegments("od550aer"))
                    }
                });
            }
        },
        "currents": {
            matches: _.matches({param: "ocean"}),
            create: function(attr) { 
                return buildProduct({
                    field: "vector",
                    type: "currents",
                    description: localize({
                        name: {en: "Current", kr: "유속", ja: "海流"},
                        qualifier: {en: " @ Surface", ja: " @ 地上"}
                    }),     
                            
                    paths: [gfs1p0degPath(attr, "currents")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var uData = file[0].data, vData = file[1].data;
                        return {
                            header: file[0].header,
                            interpolate: bilinearInterpolateVector,
                            data: function(i) {
                                var u = uData[i], v = vData[i];
                                return µ.isValue(u) && µ.isValue(v) ? [u, v] : null;
                            }
                        }
                    },

                    units: getUnitList("currents"),

                    scale: {
                        bounds: [0, 1.5],
                        customBounds: customBounds("currents"),
                        colorSegments: currentsColorSegmentrs,
                        gradient: µ.segmentedColorScale(currentsColorSegmentrs, customSegments("currents"))
                    },
                    particles: {velocityScale: 1/4400, maxIntensity: 0.7}
                });
            }
        },
        "sea_ice_area": {
            matches: _.matches({param: "ocean", overlayType: "sea_ice_area"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "sea_ice_area",
                    description: localize({
                        name: {en: "Sea-ice", kr: "해빙", ja: "雲水量"},
                        // name: {en: "Sea Ice Area", kr: "해빙", ja: "雲水量"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "sea_ice_area")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("sea_ice_area"),
                    scale: {
                        bounds: [0, 100],
                        customBounds: customBounds("sea_ice_area"),
                        colorSegments: seaIceAreaColorSegments,
                        gradient: µ.segmentedColorScale(seaIceAreaColorSegments, customSegments("sea_ice_area"))
                    }
                });
            }
        },
        "salnt": {
            matches: _.matches({param: "ocean", overlayType: "salnt"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "salnt",
                    description: localize({
                        name: {en: "salinity", kr: "염분", ja: "塩分"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "salnt")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("salnt"),
                    scale: {
                        bounds: [15, 41],
                        customBounds: customBounds("salnt"),
                        colorSegments: salntColorSegments,
                        gradient:
                            µ.segmentedColorScale(salntColorSegments, customSegments("salnt"))
                    }
                });
            }
        },
        "sst": {
            matches: _.matches({param: "ocean", overlayType: "sst"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "sst",
                    description: localize({
                        name: {en: "sea level temperature", kr: "바다온도", ja: "海の温度"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "sst")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("sst"),
                    scale: {
                        bounds: [-2, 31],
                        customBounds: customBounds("sst"),
                        colorSegments: sstColorSegments,
                        gradient:
                            µ.segmentedColorScale(sstColorSegments, customSegments("sst"))
                    }
                });
            }
        },
        "slr": {
            matches: _.matches({param: "ocean", overlayType: "slr"}),
            create: function(attr) {
                return buildProduct({
                    field: "scalar",
                    type: "slr",
                    description: localize({
                        name: {en: "sea level rise", kr: "해수면 높이", ja: "海水高"},
                        qualifier: ""
                    }),
                    paths: [gfs1p0degPath(attr, "slr")],
                    date: gfsDate(attr),
                    builder: function(file) {
                        var record = file[0], data = record.data;
                        return {
                            header: record.header,
                            interpolate: bilinearInterpolateScalar,
                            data: function(i) {
                                return data[i];
                            }
                        }
                    },
                    units: getUnitList("slr"),
                    scale: {
                        bounds: [-1.8, 0.9],
                        customBounds: customBounds("slr"),
                        colorSegments: slrColorSegments,
                        gradient:
                            µ.segmentedColorScale(slrColorSegments, customSegments("slr"))
                    }
                });
            }
        },

        "off": {
            matches: _.matches({overlayType: "off"}),
            create: function() {
                return null;
            }
        }
    };

    /**
     * Returns the file name for the most recent OSCAR data layer to the specified date. If offset is non-zero,
     * the file name that many entries from the most recent is returned.
     *
     * The result is undefined if there is no entry for the specified date and offset can be found.
     *
     * UNDONE: the catalog object itself should encapsulate this logic. GFS can also be a "virtual" catalog, and
     *         provide a mechanism for eliminating the need for /data/weather/current/* files.
     *
     * @param {Array} catalog array of file names, sorted and prefixed with yyyyMMdd. Last item is most recent.
     * @param {String} date string with format yyyy/MM/dd or "current"
     * @param {Number?} offset
     * @returns {String} file name
     */
    function lookupOscar(catalog, date, offset) {
        offset = +offset || 0;
        if (date === "current") {
            return catalog[catalog.length - 1 + offset];
        }
        var prefix = µ.ymdRedelimit(date, "/", ""), i = _.sortedIndex(catalog, prefix);
        i = (catalog[i] || "").indexOf(prefix) === 0 ? i : i - 1;
        return catalog[i + offset];
    }

    function oscar0p33Path(catalog, attr) {
        var file = lookupOscar(catalog, attr.date);
        return file ? [staticVariable.OSCAR_PATH, file].join("/") : null;
    }

    function oscarDate(catalog, attr) {
        var file = lookupOscar(catalog, attr.date);
        var parts = file ? µ.ymdRedelimit(file, "", "/").split("/") : null;
        return parts ? new Date(Date.UTC(+parts[0], parts[1] - 1, +parts[2], 0)) : null;
    }

    /**
     * @returns {Date} the chronologically next or previous OSCAR data layer. How far forward or backward in
     * time to jump is determined by the step and the catalog of available layers. A step of ±1 moves to the
     * next/previous entry in the catalog (about 5 days), and a step of ±10 moves to the entry six positions away
     * (about 30 days).
     */
    function oscarStep(catalog, date, step) {
        var file = lookupOscar(catalog, µ.dateToUTCymd(date, "/"), step > 1 ? 6 : step < -1 ? -6 : step);
        var parts = file ? µ.ymdRedelimit(file, "", "/").split("/") : null;
        return parts ? new Date(Date.UTC(+parts[0], parts[1] - 1, +parts[2], 0)) : null;
    }

    function dataSource(header) {
        // noinspection FallthroughInSwitchStatementJS
        switch (header.center || header.centerName) {
            case 5:
                return header.centerName;
            case -3:
                return "OSCAR / Earth & Space Research";
            case 7:
            case "US National Weather Service, National Centres for Environmental Prediction (NCEP)":
                return "GFS / NCEP / US National Weather Service";
            default:
                return header.centerName;
        }
    }

    function bilinearInterpolateScalar(x, y, g00, g10, g01, g11) {
        var rx = (1 - x);
        var ry = (1 - y);
        return g00 * rx * ry + g10 * x * ry + g01 * rx * y + g11 * x * y;
    }

    function bilinearInterpolateVector(x, y, g00, g10, g01, g11) {
        var rx = (1 - x);
        var ry = (1 - y);
        var a = rx * ry,  b = x * ry,  c = rx * y,  d = x * y;
        var u = g00[0] * a + g10[0] * b + g01[0] * c + g11[0] * d;
        var v = g00[1] * a + g10[1] * b + g01[1] * c + g11[1] * d;
        return [u, v, Math.sqrt(u * u + v * v)];
    }

    /**
     * Builds an interpolator for the specified data in the form of JSON-ified GRIB files. Example:
     *
     *     [
     *       {
     *         "header": {
     *           "refTime": "2013-11-30T18:00:00.000Z",
     *           "parameterCategory": 2,
     *           "parameterNumber": 2,
     *           "surface1Type": 100,
     *           "surface1Value": 100000.0,
     *           "forecastTime": 6,
     *           "scanMode": 0,
     *           "nx": 360,
     *           "ny": 181,
     *           "lo1": 0,
     *           "la1": 90,
     *           "lo2": 359,
     *           "la2": -90,
     *           "dx": 1,
     *           "dy": 1
     *         },
     *         "data": [3.42, 3.31, 3.19, 3.08, 2.96, 2.84, 2.72, 2.6, 2.47, ...]
     *       }
     *     ]
     *
     */
    function buildGrid(builder) {
        // var builder = createBuilder(data);
        var header = builder.header;
        var λ0 = header.lo1, φ0 = header.la1;  // the grid's origin (e.g., 0.0E, 90.0N)
        var Δλ = header.dx, Δφ = header.dy;    // distance between grid points (e.g., 2.5 deg lon, 2.5 deg lat)
        var ni = header.nx, nj = header.ny;    // number of grid points W-E and N-S (e.g., 144 x 73)
        var nx = ni, ny = nj;
        var date = new Date(header.refTime);
        date.setHours(date.getHours() + header.forecastTime);

        // Scan mode 0 assumed. Longitude increases from λ0, and latitude decreases from φ0.
        // http://www.nco.ncep.noaa.gov/pmb/docs/grib2/grib2_table3-4.shtml
        var grid = [], p = 0;
        var isContinuous = Math.floor(ni * Δλ) >= 360;
        var uData = [], vData = [];

        log.time("build data");
        
        let isArray = Array.isArray(builder.data(0));
        let temp;

        for (var j = 0; j < nj; j++) {
            var row = [];
            for (var i = 0; i < ni; i++, p++) {
                if(isArray){
                    temp = builder.data(p);
                    // if(isNaN(temp[0])) {
                    //     row[i] = [0, 0];
                    // } else {
                        row[i] = temp;
                    // }
                } else {
                    temp = builder.data(p);
                    // if(isNaN(temp)) {
                    //     row[i] = 0;
                    // } else {
                        row[i] = temp;
                    // }
                }
                // row[i] = builder.data(p);
            }
            if (isContinuous) {
                // For wrapped grids, duplicate first column as last column to simplify interpolation logic
                row.push(row[0]);
            }
            grid[j] = row;
        }

        log.timeEnd("build data");
        
        function interpolate(λ, φ) {
            var i = µ.floorMod(λ - λ0, 360) / Δλ;  // calculate longitude index in wrapped range [0, 360)
            var j = (φ0 - φ) / Δφ;                 // calculate latitude index in direction +90 to -90

            //         1      2           After converting λ and φ to fractional grid indexes i and j, we find the
            //        fi  i   ci          four points "G" that enclose point (i, j). These points are at the four
            //         | =1.4 |           corners specified by the floor and ceiling of i and j. For example, given
            //      ---G--|---G--- fj 8   i = 1.4 and j = 8.3, the four surrounding grid points are (1, 8), (2, 8),
            //    j ___|_ .   |           (1, 9) and (2, 9).
            //  =8.3   |      |
            //      ---G------G--- cj 9   Note that for wrapped grids, the first column is duplicated as the last
            //         |      |           column, so the index ci can be used without taking a modulo.

            var fi = Math.floor(i), ci = fi + 1;
            var fj = Math.floor(j), cj = fj + 1;

            // var t00 = builder.data( ni * (fj) + (fi));
            // var t10 = builder.data( ni * (fj) + (fi + 1));
            // var t01 = builder.data( ni * (fj + 1) + (fi));
            // var t11 = builder.data( ni * (fj + 1) + (fi + 1));

            // if(Array.isArray(grid[0][0])){
                
            // } else {
            //     if([t00, t10, t01, t11].includes(192)){
            //         return 192;
            //     }
            // }

            var row;
            if ((row = grid[fj])) {
                var g00 = row[fi];
                var g10 = row[ci];
                
                if (µ.isValue(g00) && µ.isValue(g10) && (row = grid[cj])) {
                    var g01 = row[fi];
                    var g11 = row[ci];

                    if (µ.isValue(g01) && µ.isValue(g11)) {
                        // All four points found, so interpolate the value.
                        // return builder.interpolate(i - fi, j - fj, g00, g10, g01, g11);
                        return builder.interpolate(i - fi, j - fj, g00, g10, g01, g11);
                    }
                }
            }
            // console.log("cannot interpolate: " + λ + "," + φ + ": " + fi + " " + ci + " " + fj + " " + cj);
            return null;
        }

        return {
            header: header,
            source: dataSource(header),
            date: date,
            interpolate: interpolate,
            forEachPoint: function(cb) {
                for (var j = 0; j < nj; j++) {
                    var row = grid[j] || [];
                    for (var i = 0; i < ni; i++) {
                        cb(µ.floorMod(180 + λ0 + i * Δλ, 360) - 180, φ0 - j * Δφ, row[i]);
                    }
                }
            }
        };
    }

    function productsFor(attributes) {
        var attr = _.clone(attributes), results = [];
        _.values(FACTORIES).forEach(function(factory) {
            if (factory.matches(attr)) {
                results.push(factory.create(attr));
            }
        });
        return results.filter(µ.isValue);
    }

    var d3_map_prefix = "\x00", d3_map_prefixCode = d3_map_prefix.charCodeAt(0);

    function scenarioPeriod(scenario){
        return {
            startDate: "1850/01/01",
            endDate: "2100/12/01",
            dateCount: 3012,
        }
    }

    function customSegments(type) {
        let localStorage_item_name = "settings_colorScale_" + type;
        let local_colorSegments = JSON.parse(localStorage.getItem(localStorage_item_name));

        if (local_colorSegments) return local_colorSegments;
        return null;
    }

    function customBounds(type) {
        let localStorage_item_name = "settings_colorScaleBounds_" + type;
        let local_colorSegmentsBounds = JSON.parse(localStorage.getItem(localStorage_item_name));
        if (local_colorSegmentsBounds) return local_colorSegmentsBounds;
        return [];
    }

    function getUnitList(type) {
        if (type === "wind") {
            return [
                {label: "m/s",  conversion: function(x) { return x; },            precision: 2},
                {label: "km/h", conversion: function(x) { return x * 3.6; },      precision: 1},
                {label: "kn",   conversion: function(x) { return x * 1.943844; }, precision: 1},
                {label: "mph",  conversion: function(x) { return x * 2.236936; }, precision: 1}
            ]
        } else if (type === "temp") {
            return [
                {label: "°C", conversion: function(x) { return x - 273.15; },       precision: 1},
                {label: "°F", conversion: function(x) { return x * 9/5 - 459.67; }, precision: 1},
                {label: "K",  conversion: function(x) { return x; },                precision: 1}
            ]
        } else if (type === "sea_ice_area") {
            return [
                {label: "%", conversion: function(x) { return x; }, precision: 0}
            ]
        } else if (type === "total_precipitable_water") {
            return [
                {label: "mm/day", conversion: function(x) { return x; }, precision: 1}
                //{label: "kg/m²", conversion: function(x) { return x; }, precision: 3}
            ]
        } else if (type === "currents") {
            return [
                {label: "m/s",  conversion: function(x) { return x; },            precision: 2},
                {label: "km/h", conversion: function(x) { return x * 3.6; },      precision: 1},
                {label: "kn",   conversion: function(x) { return x * 1.943844; }, precision: 1},
                {label: "mph",  conversion: function(x) { return x * 2.236936; }, precision: 1}
            ]
        } else if (type === "od550aer") {
            return [
                {label: "", conversion: function(x) { return x; }, precision: 2}
            ]
        } else if (type === "salnt") {
            return [
                {label: "g/kg", conversion: function(x) { return x; }, precision: 1}
            ]
        } else if (type === "sst") {
            return [
                {label: "°C", conversion: function(x) { return x; }, precision: 1}
            ]
        } else if (type === "slr") {
            return [
                {label: "m", conversion: function(x) { return x; }, precision: 1}
            ]
        } else {
            console.log('Unknown unit type:', type)
        }
    }

    return {
        overlayTypes: d3.set(_.keys(FACTORIES)),
        productsFor: productsFor,
        scenarioPeriod: scenarioPeriod,
        getUnitList: getUnitList,
    };

})();
